// Typed wrappers around the API service — same pattern as console/ui/src/actions.ts.
// Every call attaches a real Firebase ID token (ops/CONSOLE_AUTH_SCOPE.md) --
// backend/api/main.py refuses any request without one, or without a matching
// console_principals record.

import { auth } from "./firebase";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8180";

// ONE console serves every environment. The environment is chosen here, sent with EVERY request, and
// checked again by the backend. A fresh sign-in opens in the CONSOLE environment (the real catalog data, owner
// decision 2026-10-13); a person who may not use it is moved to the sandbox automatically (see App.tsx), and a
// choice made in this tab (sessionStorage) is kept. The backend's own default for a request that names no
// environment stays the sandbox, and every write in the console environment still needs the typed word.
// "Production" is the live WooCommerce website and is never an environment name.
export type Environment = "sandbox" | "console";
const ENV_KEY = "hec.environment";
function loadEnvironment(): Environment {
  try { return sessionStorage.getItem(ENV_KEY) === "sandbox" ? "sandbox" : "console"; } catch { return "console"; }
}
let currentEnvironment: Environment = loadEnvironment();
export const getEnvironment = (): Environment => currentEnvironment;
export function setEnvironment(e: Environment) {
  currentEnvironment = e;
  try { sessionStorage.setItem(ENV_KEY, e); } catch { /* the choice just will not survive a reload */ }
}
// Actions that write. In the console environment each one needs the person to type "console" first; the backend
// refuses a console write that does not carry the confirmation.
const WRITE_ACTIONS = new Set(["categoryMapSave", "manualUpdate", "proposePriceEdit", "testInSandbox", "exclusionRuleAdd", "exclusionRuleRemove", "pricingPolicySave", "principalSave", "requestUpdate", "listingSave", "customerStateSave"]);

async function call<T>(body: Record<string, unknown>): Promise<
  { ok: true; data: T } | { ok: false; error: string }
> {
  const user = auth.currentUser;
  if (!user) return { ok: false, error: "not signed in" };
  const payload: Record<string, unknown> = { ...body, environment: currentEnvironment };
  if (currentEnvironment === "console" && WRITE_ACTIONS.has(String(body.action))) {
    const typed = window.prompt('This writes to the CONSOLE environment (the real catalog data). Type "console" to continue.');
    if (typed !== "console") return { ok: false, error: "cancelled: the console write was not confirmed" };
    payload.confirmConsole = true;
  }
  try {
    const idToken = await user.getIdToken();
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) return { ok: false, error: json.error ?? `HTTP ${res.status}` };
    return { ok: true, data: json as T };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

export type Tiles = {
  flaggedTotal: number;
  flaggedByType: Record<string, number>;
  labelVsQuantity: { stockButZero: number; nonStockButPositive: number; mfrDirectButPositive: number };   // NDC item label against quantity, counted live
  productsTotal: number;
  // Two different answers to "how many are in stock," deliberately both shown --
  // neither is picked as authoritative. See check_stock_status.py's reasoning.
  inStockByQuantity: number;
  inStockByStatusLabel: number;
  inStockExcluded: number;
  requests?: { open: number | null; overdue: number | null };
  rowIssues: { red: number; orange: number; rejected: number };   // products the feed guard KEPT but flagged, and rows it could not store
  feedFindingsTotal: number;     // problems found in the vendors' feed files by the latest feed review
  feedFindingsHigh: number;
};

export const getTiles = () => call<Tiles>({ action: "tiles" });

// Flagged rows to review in the console before any export: each flag with the product it points at.
export type FlagRow = { docId: string; flag: Record<string, unknown>; product: Record<string, unknown> | null };
export const listFlags = (issueType: string, opts: { cursor?: unknown; limit: number }) =>
  call<{ issueType: string; label: string; rows: FlagRow[]; nextCursor: unknown; total: number; project: string }>({
    action: "flagsList",
    issueType,
    limit: opts.limit,
    cursor: opts.cursor,
  });

// The latest feed review: problems in the vendors' files, with what to ask each vendor to fix. Internal; sending is a human step.
export type FeedFinding = {
  docId: string; vendor: string; file: string; records: number; run_date: string; finding_id: string;
  severity: "high" | "medium" | "low"; title: string; detail: string; count: number; total: number;
  examples: string[]; request: string; status: string;
};
export type MappingRow = {
  feed: string; feedName: string; field: string; usedBy: string; source: string; transform: string;
  state: "captured" | "not_captured" | "absent"; confirm: string;
};
export const getFeedMapping = () =>
  call<{ legend: MappingRow[]; review: FeedFinding[]; project: string; runDate: string }>({ action: "feedMapping" });
export type FeedFile = {
  docId: string; feed: string; path: string; kind: "catalog" | "inventory"; job: string; state: string; error: string | null;
  vendor_size: number | null; vendor_modified: string | null; first_seen_at: string | null; last_checked_at: string | null; last_changed_at: string | null;
  unchanged_checks: number | null; ingested_modified: string | null; ingested_at: string | null; last_ingest_status: string | null;
  last_ingest_finished_at: string | null; last_ingest_note: string | null; consecutive_failures: number | null;
  learnedGapMinutes: number | null; learnedRegular: boolean; checkMode: string; nextCheckAt: string | null; changesObserved: number;
  health: "green" | "orange" | "red"; needsIngest: boolean;
};
export type WatcherFailure = { docId: string; at: string; kind: string; feed: string | null; job: string | null; message: string };
export type FeedHistory = { docId: string; feed: string; path: string; kind: string; vendor_size: number; vendor_modified: string;
  previous_modified: string | null; detected_at: string; lag_seconds: number; gap_seconds: number | null; first_observation: boolean };
export type FeedChange = { docId: string; feed: string; job: string; mode: string; trigger: string; status: string; note?: string; started_at: string;
  finished_at: string; seconds?: number; written?: number; updated?: number; products_in_feed?: number | null;
  plan?: { new: number; changed: number; unchanged: number; gone: number; to_write: number; to_update: number; field_changes: Record<string, number> };
  files?: { path: string; size: number; modified: string }[]; first_run_backfill?: boolean };
export type WatcherTick = { docId: string; at: string; seconds: number; checked: string[]; skipped_not_due: string[]; started: Record<string, number>;
  started_why: Record<string, string[]>; held: Record<string, string>; errors: Record<string, string>; changes: number; failures_logged: number };
export type ImageRun = { docId: string; feed: string; started_at: string; seconds: number; records: number; fetched: number; stored: number;
  with_problems: number; no_bytes: number; mb: number; manifest: string };
export type Heartbeat = { last_tick_at: string; last_tick_seconds: number; last_checked: string[]; last_errors: Record<string, string> };
export type FeedHealth = { files: FeedFile[]; history: FeedHistory[]; changes: FeedChange[]; failures: WatcherFailure[]; ticks: WatcherTick[];
  imageRuns: ImageRun[]; heartbeat: Heartbeat | null; settings: Record<string, { check_every_minutes: number }>; project: string; now: string };
export const getFeedHealth = () => call<FeedHealth>({ action: "feedHealth" });
export const listFeedFindings = () =>
  call<{ rows: FeedFinding[]; project: string; runDate: string }>({ action: "feedFindings" });

export const generateExport = (issueType: string) =>
  call<{ issueType: string; rowCount: number; csv: string }>({
    action: "export",
    issueType,
  });

// Pending product edits -- ops/PRODUCT_EDIT_WORKFLOW_SCOPE.md's proposal
// path. NEVER mutates the live product or touches WooCommerce -- writes a
// reviewable proposal only. Price-only for now (see actions.ts/main.py's
// own note on shipping-class editing needing a corpus field first).
export type PriceEditProposal = {
  proposalId: string;
  sourceDocId: string;
  sku: string;
  sourceId: string;
  priceFrom: number | null;
  priceProposed: number;
  priceFinal: number;
  priceMapClamped: boolean;
  mapPriceAtProposal: number | null;
  duplicateGroupBlocked: boolean;
  status: string;
  proposedAt: string;
};

export const proposePriceEdit = (docId: string, price: number) =>
  call<PriceEditProposal>({ action: "proposePriceEdit", docId, price });

export const listPendingEdits = () =>
  call<{ rows: PriceEditProposal[]; rowCount: number }>({ action: "pendingEditsList" });

// Applies ONE proposal's final price to the local hec_sandbox DB only --
// never production, never InMotion. Only works when backend/api/main.py runs on
// a machine where hec_sandbox exists (ISSUES.md #22).
export type SandboxTestResult = {
  proposalId: string; sku: string; stagingPostId: number;
  rowsUpdated: number; readback: Record<string, string>;
};

export const testInSandbox = (proposalId: string) =>
  call<SandboxTestResult>({ action: "testInSandbox", proposalId });

// Shipping matrix -- read-only visualization of shipping_class x zone x
// price-tier structure (ISSUES.md #23). No write path -- staged-first only.
export type ShippingClass = { id: number; name: string; slug: string; productCount: number };
export type ShippingZoneMethod = { instanceId: number; methodId: string; isEnabled: boolean };
export type ShippingZone = {
  zoneId: number; zoneName: string; methods: ShippingZoneMethod[];
  hasNoEnabledMethod: boolean; hasFreeShipping: boolean; hasFlatRate: boolean;
};
export type ShippingRateRule = {
  rateClass: string; min: string; max: string; cost: string;
  costPerItem: string; costPerWeightUnit: string; costPercent: string; label: string;
};
export type ShippingMatrix = {
  shippingClasses: ShippingClass[];
  zones: ShippingZone[];
  matrix: Record<string, Record<string, ShippingRateRule[]>>; // zoneId -> classId -> rules
};

export const getShippingMatrix = () => call<ShippingMatrix>({ action: "shippingMatrix" });

// Data health -- LIVE WooCommerce DB queries (table sizes, duplicate
// products), not Firestore. See ops/DATA_CLEANUP_TOOLING_SCOPE.md. Needs a
// working SSH path to the WooCommerce server -- may fail with a real error
// if run from an environment InMotion's firewall doesn't allow yet.
export type DbHealthTable = { tableName: string; sizeMb: number; rowsApprox: number };
export type DuplicateCopy = {
  postId: number;
  status: string;
  modified: string;
  price: string | null;
  stock: string | null;
  stockStatus: string | null;
};
export type DuplicateGroup = { sku: string; copyCount: number; copies: DuplicateCopy[] };
export type DbHealth = {
  tables: DbHealthTable[];
  totalMb: number;
  tableCount: number;
  duplicateSkuCount: number;
  duplicateGroups: DuplicateGroup[];
};

export const getDbHealth = () => call<DbHealth>({ action: "dbHealth" });

// Which feed the Sheet is showing ("" = all).
export type ProductSourceFilter = "" | "ndc" | "dnh" | "kroll" | "nms" | "ors" | "msc" | "shop" | "excluded";

export type ManualProductRow = {
  docId: string;
  sku: string;
  name: string;
  description: string;
  price: number | null;
  quantityOnHand: number | null;
  category: string;
  manufacturerName: string;
  gtin: string;
  sourceId: string;
  manuallyEdited: boolean;
};

export const NMS_BRAND_KEYS = [
  "ecat", "julabo", "powerfilm", "xikar", "mjm", "extras", "abilityone",
  "otc_tools", "moore_unique", "wahl", "baileigh", "goal_zero",
] as const;

export const listManualProducts = (brandKey?: string) =>
  call<{ rows: ManualProductRow[]; rowCount: number }>({
    action: "manualList",
    ...(brandKey ? { brandKey } : {}),
  });

// Only the fields the console table lets staff edit -- matches backend/api/main.py's
// EDITABLE_FIELDS exactly; the service itself also enforces this, this is just
// the client-side mirror so the type system catches a typo before a round trip.
export type EditableManualFields = Partial<{
  name: string;
  description: string;
  price: number;
  quantityOnHand: number;
  category: string;
  manufacturerName: string;
}>;

// The UI's own field names are camelCase; the service's Firestore-backed shape
// uses quantity_on_hand/manufacturer_name -- translated here, once, so callers
// never have to remember which casing which layer expects.
const TO_SERVICE_FIELD: Record<string, string> = {
  quantityOnHand: "quantity_on_hand",
  manufacturerName: "manufacturer_name",
};

export const updateManualProduct = (docId: string, fields: EditableManualFields) =>
  call<{ docId: string; updated: string[] }>({
    action: "manualUpdate",
    docId,
    fields: Object.fromEntries(
      Object.entries(fields).map(([k, v]) => [TO_SERVICE_FIELD[k] ?? k, v])
    ),
  });

// Sheet -- the analysis table (ops/CORPUS_SCHEMA_CONTRACT.md section 7). Read-only.
// Each tile is a filter: clicking it lists exactly the rows it counts.
export type SheetTile = {
  key: string;
  label: string;
  count: number | null;
  listable: boolean;
  pending?: boolean;
  note?: string;
};
export type SheetTiles = { source: string; project: string; tiles: SheetTile[] };
export type RowIssue = { severity: "red" | "orange"; reasons: string[] };
export type DerivedRow = {
  sellPrice: number | null; marginPct: number | null; inStock: boolean; wc: "ready" | "review" | "blocked" | "container"; gmc: "ready" | "review" | "blocked" | "container";
  wcBlocks: string[]; gmcBlocks: string[]; priceFlags: string[]; stored: boolean;
};
export type SheetRow = { docId: string; fields: Record<string, unknown>; issue?: RowIssue | null; derived?: DerivedRow; typed?: string[] };

// Products (or unstorable rows) the feed guard flagged, a page at a time, for review before anything is exported.
export type RowIssueRow = { docId: string; issue: Record<string, unknown>; product: Record<string, unknown> | null };
export const listRowIssues = (severity: "red" | "orange" | "rejected", cursor?: unknown, limit = 100) =>
  call<{ severity: string; rows: RowIssueRow[]; nextCursor: unknown; total: number; project: string }>({
    action: "rowIssuesList", severity, limit, ...(cursor ? { cursor } : {}),
  });

export const getSheetTiles = (source: ProductSourceFilter) =>
  call<SheetTiles>({ action: "sheetTiles", source });

// Sorting runs on the SERVER across the whole collection, one page at a time.
export type SheetSortKey = "sku" | "name" | "manufacturer" | "mpn" | "cost" | "price" | "map" | "qty";
export type SheetSort = { key: SheetSortKey; dir: "asc" | "desc" };

export const listSheet = (
  source: ProductSourceFilter,
  filter: string,
  opts: { cursor?: unknown; limit: number; sort?: SheetSort | null },
) =>
  call<{ rows: SheetRow[]; nextCursor: unknown; project: string }>({
    action: "sheetList",
    source,
    filter,
    limit: opts.limit,
    cursor: opts.cursor,
    ...(opts.sort ? { sort: opts.sort.key, direction: opts.sort.dir } : {}),
  });

// One search box over SKU, part number, GTIN and manufacturer (NSN once captured): normalized, ranked, best 100.
export type SearchRow = SheetRow & { matchedOn: string; exact: boolean };
export const searchProducts = (source: ProductSourceFilter, q: string) =>
  call<{ rows: SearchRow[]; truncated: boolean; query: string; project: string }>({
    action: "productSearch",
    source,
    q,
    limit: 100,
  });


// Exclusion rules: brands a vendor asked us to stop selling, per feed. Built-in rules are code; the rest are added here.
export type ExclusionRule = {
  id: string; sourceId: string; feed: string; kind: string; value: string; origin: string;
  active: boolean; removable: boolean; matches: number | null; note?: string;
  potential?: { name: string; count: number }[];   // names in the same feed that look like this rule's brand but no rule covers
};
export type ExclusionCheck = {
  matches: number; spellings: { value: string; count: number }[]; similar: { value: string; count: number }[];
  duplicate?: boolean; warning?: string; id?: string;
};
export const listExclusionRules = () =>
  call<{ rows: ExclusionRule[]; feeds: Record<string, string> }>({ action: "exclusionRules" });
export const checkExclusionRule = (sourceId: string, value: string) =>
  call<ExclusionCheck>({ action: "exclusionRuleCheck", sourceId, value });
export const addExclusionRule = (sourceId: string, value: string, note: string, urgency = "24h") =>
  call<ExclusionCheck>({ action: "exclusionRuleAdd", sourceId, value, note, urgency });
export const removeExclusionRule = (id: string, urgency = "24h") =>
  call<{ id: string; active: boolean }>({ action: "exclusionRuleRemove", id, urgency });

export type ManufacturerRow = { sourceId: string; feed: string; name: string; count: number; excluded: boolean };
export const listManufacturers = (sourceId: string, q: string) =>
  call<{ rows: ManufacturerRow[]; total: number; truncated: boolean; updated: Record<string, string>; missing: string[] }>(
    { action: "manufacturers", sourceId, q, limit: 1000 });

// The derived layer: selling price and the two approvals (WooCommerce, Merchant Center), judged from what the console ingested.
export type DerivedTiles = {
  source: string; total: number; counts: Record<string, number>; reasons: Record<string, number>;
  policyVersion: number; policyConfirmed: boolean; derive: { needed?: boolean; reason?: string; last_full_at?: string };
};
export const getDerivedTiles = (source: ProductSourceFilter) => call<DerivedTiles>({ action: "derivedTiles", source });
export type ReadinessCheck = { id: string; label: string; result: "pass" | "fail" | "warn" | "pending"; severity: "block" | "warn" | "pending"; detail: string };
export type ReadinessDetail = {
  docId: string; inStock: boolean; wc: { status: string; checks: ReadinessCheck[] }; gmc: { status: string; checks: ReadinessCheck[] };
  price: { sell_price: number | null; margin_pct: number | null; margin: number | null; markup_pct: number | null; rule: string | null; flags: string[] };
  policy: { version: number; confirmed: boolean }; storedLayer: boolean;
};
export const getReadinessDetail = (docId: string) => call<ReadinessDetail>({ action: "readinessDetail", docId });

export type PricingPolicy = {
  default_markup_pct: number; min_margin_pct: number; rounding: "x.99" | "none"; cap_to_list: boolean;
  overrides: { scope: "feed" | "brand" | "product"; key: string; markup_pct: number }[]; confirmed?: boolean; version?: number; updated_by?: string; updated_at?: string; note?: string;
};
export const getPricingPolicy = () =>
  call<{ policy: PricingPolicy; history: Record<string, unknown>[]; defaults: Record<string, unknown> }>({ action: "pricingPolicy" });
export const savePricingPolicy = (p: PricingPolicy, note: string) => call<{ version: number }>({ action: "pricingPolicySave", ...p, note });

// Who is signed in and what they may do; and (admin only) the people list.
export type Capability = { key: string; label: string; group: string };
export type Me = { email: string; class: "admin" | "staff"; caps: string[]; capabilities: Capability[] };
export const getMe = () => call<Me>({ action: "me" });
export type Principal = { email: string; class: "admin" | "staff"; caps: string[]; disabled: boolean; updated_by: string; updated_at: string; note: string };
export const listPrincipals = () => call<{ rows: Principal[]; capabilities: Capability[] }>({ action: "principalsList" });
export const savePrincipal = (p: { email: string; class: "admin" | "staff"; caps: string[]; disabled: boolean; note?: string }) =>
  call<{ email: string }>({ action: "principalSave", ...p });

export type ChangeRequest = {
  id: string; type: string; summary: string; source_id: string; value: string; urgency: string; requested_at: string; due_at: string; requested_by: string;
  note: string; woocommerce_done_at: string; woocommerce_done_by: string; merchant_done_at: string; merchant_done_by: string;
  consoleApplied: boolean; wcDone: boolean; gmcDone: boolean; isClosed: boolean; overdue: boolean; progress?: { matches: number; excludedInLayer: number };
};
export const listRequests = (status: "open" | "closed" | "all") => call<{ rows: ChangeRequest[] }>({ action: "requestsList", status });
export const updateRequest = (id: string, step: "woocommerce" | "merchant", done: boolean) =>
  call<{ id: string }>({ action: "requestUpdate", id, step, done });

// The listing editor: feed value, dynamic default, typed override and final value per field. Reset removes an override.
export type ListingField = "title" | "slug" | "description" | "brand" | "category" | "sell_price" | "seo_title" | "seo_description" | "focus_keyphrase" | "robots" | "canonical_path";
export type ListingView = {
  docId: string; feed: Record<string, string>; dynamic: Record<string, string>; override: Partial<Record<ListingField, string | number>>;
  final: Record<string, { value: string; source: "override" | "dynamic" }>;
  price: { rule: number | null; override?: number; cost: number | null; map: number | null; flags: string[] }; editedBy: string; editedAt: string;
};
export const getListing = (docId: string) => call<ListingView>({ action: "listingGet", docId });
export const saveListing = (docId: string, fields: Record<string, string | number>, resetAll = false) =>
  call<{ docId: string }>({ action: "listingSave", docId, fields, resetAll });

export type StripDay = { date: string; state: "ok" | "failed" | "partial" | "none" | "missing_business" | "missing_weekend"; ok: number; failed: number };
export type Strip = { days: StripDay[]; from: string | null; to: string; expected: number; covered: number; missing: { date: string; weekday: string; weekend: boolean }[]; missingBusiness: number };
export type StatusRow = { group: string; name: string; status: "ok" | "warn" | "bad" | "na"; lastOk: string; lastFail: string; detail: string; op?: string | null; strip?: Strip };
export const getStatus = () => call<{ rows: StatusRow[]; now: string }>({ action: "status" });

// Customers (CRM): loaded from the exported store database; personal data, so every call needs the customers capability and exporting needs its own.
export type CustomerTile = { key: string; group: string; label: string; count: number | null };
export type Customer = {
  customer_key: string; email: string; first_name: string; last_name: string; company: string; phone: string; city: string; state: string; postcode: string; country: string;
  billing: Record<string, string>; shipping: Record<string, string>; registered: boolean; role: string; user_registered: string; last_active: string; as_of: string;
  orders_total: number; orders_completed: number; orders_processing: number; orders_refunded: number; orders_cancelled: number; orders_failed: number; orders_on_hold: number;
  orders_pending: number; orders_preorder: number; orders_net: number; gross_revenue: number; refunded_total: number; ltv: number; aov: number;
  first_order: string; last_order: string; last_net_order: string; days_since_last: number | null; median_gap_days: number | null;
  segment: string; rfm_r: number; rfm_f: number; rfm_m: number; rfm_segment: string; min_fraud_score: number | null; distinct_ips: number;
  junk_reasons: string[]; payment_methods: string[]; sources: string[]; top_items: string[]; email_domain: string;
  [flag: `flag_${string}`]: boolean;
  crm?: { cleanup: string; tags: string[]; notes: number };   // what the console's people recorded (the customer's own `state` field is the US state)
};
export type CustomerOrder = { order_id: number; date: string; status: string; total: number; refunded: number; payment: string; items: string[]; item_count: number;
  source: string; utm_source: string; device: string; fraud_score: number | null; proxy: boolean; attack: boolean; created_via: string };
export type CustomerNote = { at: string; by: string; text: string };
export const getCustomerTiles = () => call<{ tiles: CustomerTile[]; meta: { as_of?: string; customers?: number; orders?: number; loaded_at?: string; source?: string } }>({ action: "customersTiles" });
export const listCustomers = (filter: string, q: string, cursor: unknown, limit: number) =>
  call<{ rows: Customer[]; nextCursor: unknown }>({ action: "customersList", filter, q, cursor, limit });
export const getCustomer = (key: string) =>
  call<{ customer: Customer; orders: CustomerOrder[]; state: { cleanup: string; tags: string[]; follow_up: string; notes: CustomerNote[] } }>({ action: "customerDetail", key });
export const saveCustomerState = (key: string, patch: { cleanup?: string; tags?: string[]; follow_up?: string; note?: string }) =>
  call<{ key: string }>({ action: "customerStateSave", key, ...patch });
export const exportCustomers = (filter: string, cursor: unknown) => call<{ rows: Record<string, unknown>[]; nextCursor: unknown }>({ action: "customersExport", filter, q: "", cursor, limit: 200 });

export type AnalyticsReport<T> = { built: boolean; as_of?: string; built_at?: string; customers?: number; orders?: number; report?: T };
export const getAnalyticsReport = <T,>(name: "sales" | "customers" | "product_sales" | "marketing" | "ga4") => call<AnalyticsReport<T>>({ action: "analyticsReport", name });
export type Ga4Live = { ok: true; asOf: string; last5: number; last30: number; countries: { name: string; users: number }[]; devices: { name: string; users: number }[]; pages: { name: string; users: number }[] } | { ok: false; error: string };
export const getGa4Realtime = () => call<Ga4Live>({ action: "ga4Realtime" });
export type AnalyticsProducts = {
  feeds: Record<string, { total: number; counts: Record<string, number>; reasons: Record<string, number> }>;
  all: { total: number; counts: Record<string, number>; reasons: Record<string, number> }; policyConfirmed: boolean; derive: { needed?: boolean; reason?: string; last_full_at?: string };
};
export const getAnalyticsProducts = () => call<AnalyticsProducts>({ action: "analyticsProducts" });

export type ProductSales = { sold: boolean; units?: number; revenue?: number; orders?: number; first?: string; last?: string; methods?: string[]; name?: string; built_at?: string };
export const getProductSales = (docId: string) => call<ProductSales>({ action: "productSales", docId });


// The in-stock snapshot: every in-stock product as one compact row (collector/sheet_snapshot.py COLUMNS), loaded once; filtering, search and sorting by any
// number of columns then happen in the browser.
export type InStockData = { built: boolean; columns: string[]; rows: unknown[][]; labels: Record<string, string>; updatedAt: string };
export type InStockReply = InStockData | { unchanged: true; updatedAt: string };
export const getInStock = (since?: string) => call<InStockReply>({ action: "sheetInstock", ...(since ? { since } : {}) });
export const getProductRow = (docId: string) => call<{ row: SheetRow; project: string }>({ action: "productGet", docId });


// The category mapping: what each feed's category is filed under in WooCommerce and in Google's taxonomy. Only a CONFIRMED row counts as a pass in the approvals.
export type CategoryRow = {
  id: string; source_id: string; feed_category: string; products: number; in_stock: number; store_category: string; store_basis: string; store_confidence: number;
  google_category_id: number | null; google_category: string; google_confidence: number; status: "proposed" | "unmapped" | "confirmed"; confirmed_by?: string; confirmed_at?: string;
};
export type CategoryEdit = { id: string; status?: CategoryRow["status"]; store_category?: string; google_category?: string; google_category_id?: number | null };
export const listCategoryMap = () => call<{ rows: CategoryRow[] }>({ action: "categoryMapList" });
export const saveCategoryMap = (rows: CategoryEdit[]) => call<{ saved: number; queued: number | string }>({ action: "categoryMapSave", rows });


// The production archive: evidence-only records of every product the old store carried, until each is kept, reconciled or dropped.
export type ArchiveRow = {
  id: string; shop_id: string; sku: string; status: string; title: string; slug: string; old_url_guess: string; regular_price: number | null; price: number | null;
  store_stock_status: string; store_stock_qty: string; categories: string[]; brand: string; tags: string[]; images: string[]; console_matches: string[]; console_sources: string[];
  console_match_count: number; orders: number; last_ordered: string; shop_only: boolean; disposition: string; modified: string;
  console_status?: string; console_blockers?: string[]; console_doc?: string;
};
export const listArchive = (filter: string, sku: string, cursor?: unknown, summary = false) =>
  call<{ rows: ArchiveRow[]; nextCursor: unknown; summary?: Record<string, number> }>({ action: "archiveList", filter, sku, limit: 100, summary, ...(cursor ? { cursor } : {}) });
